import {
    EndpointRegistry,
    extractEndpointsToBeInvalidated,
    extractEndpointsToBeRemoved,
    HttpOptions,
    RegisteredHttpEndpoint,
} from '@luminovo/api-contracts';
import { getToken } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import { httpQueryKey, useDebugErrorHandler } from '@luminovo/data-stack';
import { http } from '@luminovo/http-client';
import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { z } from 'zod/v4';

import { HasPermissionForEndpointFn, useHasModulePermissions } from '@/shared/permissions/useModulePermissions';

/** Applies the endpoint definition's `invalidate` and `remove` arrays after a successful mutation. */
export function useHttpMutation<T extends RegisteredHttpEndpoint>(
    endpoint: T,
    options: Pick<
        UseMutationOptions<z.infer<EndpointRegistry[T]['responseBody']>, unknown, HttpOptions<T>, unknown>,
        'retry' | 'onSuccess'
    > & {
        /** Shown after a successful mutation. */
        snackbarMessage:
            | ((response: { responseBody: z.infer<EndpointRegistry[T]['responseBody']> }) => {
                  message: string;
                  variant: 'success' | 'error' | 'info';
              })
            | string
            | null;
        /** Skips the default error handler, e.g. when a FormContainer shows the error. */
        disableOnError?: boolean;
        /** Invalidates all queries after a `DELETE` endpoint. @default true */
        enableInvalidateAllQueries?: boolean;
        /** Milliseconds to wait before invalidating, for a backend that doesn't update immediately. */
        delay?: number;
    },
): UseMutationResult<z.infer<EndpointRegistry[T]['responseBody']>, unknown, HttpOptions<T>, unknown> & {
    checkPermission: HasPermissionForEndpointFn<T>;
} {
    const {
        snackbarMessage,
        onSuccess = () => {},
        disableOnError = false,
        enableInvalidateAllQueries = true,
        delay,
        ...rest
    } = options;
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const onError = useDebugErrorHandler();

    const { permissionForEndpoint } = useHasModulePermissions();
    const { checkPermission } = permissionForEndpoint(endpoint);

    const mutation = useMutation({
        mutationKey: httpQueryKey(endpoint),
        mutationFn: (variables: HttpOptions<T>) => http(endpoint, variables, getToken()),
        onSuccess: async (data, variables, onMutateResult, context) => {
            if (isPresent(delay)) {
                await new Promise((resolve) => setTimeout(resolve, delay));
            }

            // Inactive queries aren't refetched, so a deletion removes them.
            if (endpoint.startsWith('DELETE /')) {
                await queryClient.removeQueries({ type: 'inactive' });
                if (enableInvalidateAllQueries) {
                    queryClient.invalidateQueries();
                }
            }

            await Promise.allSettled(
                extractEndpointsToBeRemoved(endpoint).map((endpoint) =>
                    queryClient.removeQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            await Promise.allSettled(
                extractEndpointsToBeInvalidated(endpoint).map((endpoint) =>
                    queryClient.invalidateQueries({
                        queryKey: [endpoint],
                    }),
                ),
            );

            const message =
                typeof snackbarMessage === 'function'
                    ? snackbarMessage({ responseBody: data })
                    : typeof snackbarMessage === 'string'
                      ? { message: snackbarMessage, variant: 'success' as const }
                      : null;

            if (message) {
                enqueueSnackbar(message.message, {
                    key: message.message,
                    variant: message.variant,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    autoHideDuration: 2000,
                    preventDuplicate: true,
                });
            }

            return onSuccess(data, variables, onMutateResult, context);
        },
        onError: disableOnError ? undefined : onError,
        ...rest,
    });

    return { checkPermission, ...mutation };
}
